import * as yup from 'yup';
import { useFormik } from 'formik';
import { Box } from '@mui/material';
import { useMemo, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { TextField, AlertBoxMsg } from '@qrios/frontend-library';

import {
  callbackValidation,
  countryValidation,
  emailValidation,
  msisdnValidationSimpleRequired,
  nameValidation
} from '../../../../../../constants/validation';
import locales from '../../../../../../constants/locales';
import { USER_PROFILE } from '../../../../../../constants/storage';
import { UserProfile } from '../../../../../authentication/interfaces';
import { ACCOUNT_INFORMATION } from '../../../../../../constants/formInputs';
import { nortadaStorage } from '../../../../../../constants/helpers/storage';
import { AlertBeforeOtpSendInCreateForrm } from '../../../../../common/common-alert-messages';

import { StepProps } from '../interfaces';
import StepperFooter from '../stepperFooter';
import { useCreateNewClientProfile } from '../hooks';

export const validationSchema = yup.object().shape({
  [ACCOUNT_INFORMATION.EMAIL]: emailValidation,
  [ACCOUNT_INFORMATION.NAME]: nameValidation,
  [ACCOUNT_INFORMATION.COUNTRY]: countryValidation,
  [ACCOUNT_INFORMATION.MSISDN]: msisdnValidationSimpleRequired,
  [ACCOUNT_INFORMATION.CALLBACK]: callbackValidation
});

const NewClientForm = ({ goToNextStep, onFailure }: StepProps) => {
  const {
    createNewClientProfileLoading,
    createNewClientProfileLoadingError,
    handleCreateNewClientProfile
  } = useCreateNewClientProfile();

  const theme = useTheme();
  const userProfile = useMemo(() => nortadaStorage.getItem(USER_PROFILE), []) as UserProfile;
  const initUserName = useMemo(() => {
    const { firstName, lastName } = userProfile;

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    return firstName || lastName || '';
  }, [userProfile]);

  const formik = useFormik({
    initialValues: {
      [ACCOUNT_INFORMATION.EMAIL]: userProfile?.email || '',
      [ACCOUNT_INFORMATION.NAME]: initUserName,
      [ACCOUNT_INFORMATION.COUNTRY]: userProfile.attributes?.location?.[0] ?? '',
      [ACCOUNT_INFORMATION.MSISDN]: '',
      [ACCOUNT_INFORMATION.CALLBACK]: ''
    },
    validationSchema,
    onSubmit: values => {
      const createNewClientProfileData = {
        ...values,
        msisdn: values.msisdn
      };

      handleCreateNewClientProfile({
        createNewClientProfileData,
        onSuccess: (msisdn: string, shortcode: string) => goToNextStep(msisdn, shortcode),
        onFailure
      });
    }
  });

  return (
    <Box>
      <Box width={theme.customComponents.profileCreation.warningBox.width}>
        <AlertBeforeOtpSendInCreateForrm />
      </Box>
      {createNewClientProfileLoadingError ? (
        <Box mt={2} width={theme.customComponents.profileCreation.warningBox.width}>
          <AlertBoxMsg severity={createNewClientProfileLoadingError.severity}>
            {createNewClientProfileLoadingError.message}
          </AlertBoxMsg>
        </Box>
      ) : null}
      <Box mb={1} mt={2}>
        <TextField
          name={ACCOUNT_INFORMATION.EMAIL}
          value={formik.values[ACCOUNT_INFORMATION.EMAIL]}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          label={locales.accountInformation.label.email}
          disabled={true}
          error={formik.errors[ACCOUNT_INFORMATION.EMAIL]}
          touched={formik.touched[ACCOUNT_INFORMATION.EMAIL]}
          placeholder={locales.accountInformation.placeholder.email}
        />
      </Box>
      <Box mb={1}>
        <TextField
          name={ACCOUNT_INFORMATION.NAME}
          value={formik.values[ACCOUNT_INFORMATION.NAME]}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          label={locales.accountInformation.label.name}
          error={formik.errors[ACCOUNT_INFORMATION.NAME]}
          touched={formik.touched[ACCOUNT_INFORMATION.NAME]}
          placeholder={locales.accountInformation.placeholder.name}
        />
      </Box>
      <Box mb={1}>
        <TextField
          name={ACCOUNT_INFORMATION.COUNTRY}
          value={formik.values[ACCOUNT_INFORMATION.COUNTRY]}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          label={locales.accountInformation.label.country}
          error={formik.errors[ACCOUNT_INFORMATION.COUNTRY]}
          touched={formik.touched[ACCOUNT_INFORMATION.COUNTRY]}
          placeholder={locales.accountInformation.placeholder.country}
        />
      </Box>
      <Box mb={1}>
        <TextField
          name={ACCOUNT_INFORMATION.MSISDN}
          value={formik.values[ACCOUNT_INFORMATION.MSISDN]}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          label={locales.accountInformation.label.msisdn}
          error={formik.errors[ACCOUNT_INFORMATION.MSISDN]}
          touched={formik.touched[ACCOUNT_INFORMATION.MSISDN]}
          placeholder={locales.accountInformation.placeholder.msisdn}
        />
      </Box>
      <Box mb={1}>
        <TextField
          name={ACCOUNT_INFORMATION.CALLBACK}
          value={formik.values[ACCOUNT_INFORMATION.CALLBACK]}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          label={locales.accountInformation.label.callback}
          error={formik.errors[ACCOUNT_INFORMATION.CALLBACK]}
          touched={formik.touched[ACCOUNT_INFORMATION.CALLBACK]}
          placeholder={locales.accountInformation.placeholder.callbackUrl}
        />
      </Box>
      <StepperFooter
        primaryButton={{
          text: 'Go to OTP verification',
          onClickHandler: formik.handleSubmit,
          isLoading: createNewClientProfileLoading
        }}
      />
    </Box>
  );
};

export default memo(NewClientForm);
